import type { Api } from "./api";
import type { ChatMessage } from "./client";
import type { Content } from "./content";
import type { CallInfo, CallJoin, CallMedia } from "./types";

// Call state for one signed-in device: the call it is in, a call ringing for it, and history.
//
// The media key for a call is 32 random bytes made by the caller. It reaches the other members
// only inside the chat's Megolm-encrypted messages, as a `call` content message. The server
// signals the call and hands out LiveKit tokens, but it never sees the key, and neither does
// LiveKit, which only relays frames that are already encrypted.

export type ActiveCall = {
  call: CallInfo;
  chatId: string;
  livekitUrl: string;
  token: string;
  /** The media key, base64. Hand it only to the LiveKit key provider. */
  key: string;
  /** True when this device started the call. */
  outgoing: boolean;
  joinedAt: number;
};

export type CallsState = {
  active: ActiveCall | null;
  incoming: CallInfo | null;
  history: CallInfo[];
  /** The chat or call being joined, while the request is in flight. */
  joining: string | null;
  error: string | null;
  /** The huddle going on in each channel that has one. */
  huddles: Record<string, CallInfo>;
};

export type CallSocket = {
  on(event: string, listener: (call: CallInfo) => void): unknown;
};

export type CallDeps = {
  api: Pick<Api, "startCall" | "joinCall" | "leaveCall" | "declineCall" | "callHistory" | "activeCall">;
  meId: () => string | undefined;
  /** Sends content into the chat, encrypted with the chat's Megolm session. */
  send: (chatId: string, content: Content) => Promise<void>;
  messages: (chatId: string) => ChatMessage[] | undefined;
  /** Fetches the chat's newest messages. */
  sync: (chatId: string) => Promise<void>;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  newKey?: () => string;
  timers?: {
    set: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
    clear: (t: ReturnType<typeof setTimeout>) => void;
  };
};

export class CallError extends Error {}

/** How long a call rings before it counts as missed. Matches the server. */
export const RING_MS = 45_000;
/** How long a joiner waits for the call key to arrive in the chat. */
export const KEY_WAIT_MS = 10_000;
const POLL_MS = 1_000;

export const MAX_CALL_PARTICIPANTS = 32;

const NO_KEY =
  "The key for this call has not reached this device yet, so Rhema cannot join it securely. Try again in a moment.";
const KEY_NOT_SENT =
  "Rhema could not share the key for this call with the chat. Check your connection and try again.";
const BUSY = "You are already in a call. End it before starting another.";
const ENDED = "This call has ended.";

/** A fresh call key: 32 random bytes, base64. */
export function newCallKey(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

/** The newest key the caller sent for this call, read from decrypted messages. */
export function findCallKey(messages: ChatMessage[] | undefined, call: CallInfo): string | null {
  if (!messages) return null;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    const c = m?.content;
    if (c?.type !== "call" || c.call_id !== call.id || c.event !== "started" || !c.key) continue;
    // Only the person who started the call hands out its key.
    if (call.started_by && m?.senderId !== call.started_by) continue;
    return c.key;
  }
  return null;
}

/** Whether this person missed the call. */
export function isMissedFor(call: CallInfo, userId: string | undefined): boolean {
  if (call.started_by === userId) return false;
  const me = call.participants.find((p) => p.user_id === userId);
  return (
    me?.state === "missed" || me?.state === "silenced" || (Boolean(call.ended_at) && me?.state === "ringing")
  );
}

/** Seconds between the first answer and the end, or null if the call was never answered. */
export function callDuration(call: CallInfo): number | null {
  if (!call.answered_at || !call.ended_at) return null;
  return Math.max(0, Math.round((Date.parse(call.ended_at) - Date.parse(call.answered_at)) / 1000));
}

function upsert(list: CallInfo[], call: CallInfo): CallInfo[] {
  const rest = list.filter((c) => c.id !== call.id);
  return [call, ...rest].sort((a, b) => b.started_at.localeCompare(a.started_at));
}

export class CallManager {
  state: CallsState = { active: null, incoming: null, history: [], joining: null, error: null, huddles: {} };
  private listeners = new Set<() => void>();
  /** Calls this device started. Only it announces how they ended, so the chat gets one line. */
  private startedHere = new Set<string>();
  private reported = new Set<string>();
  /** The newest version of each call the server has pushed, which can beat a slow join reply. */
  private latest = new Map<string, CallInfo>();
  private ringTimer: ReturnType<typeof setTimeout> | null = null;
  private answerTimer: ReturnType<typeof setTimeout> | null = null;
  private deps: Required<Omit<CallDeps, "timers">> & { timers: NonNullable<CallDeps["timers"]> };

  constructor(deps: CallDeps) {
    this.deps = {
      now: () => Date.now(),
      sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
      newKey: newCallKey,
      timers: { set: (fn, ms) => setTimeout(fn, ms), clear: (t) => clearTimeout(t) },
      ...deps,
    };
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = () => this.state;

  private set(patch: Partial<CallsState>) {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  attach(socket: CallSocket): void {
    socket.on("call.ring", (call) => this.onRing(call));
    socket.on("call.updated", (call) => void this.onUpdated(call));
  }

  dispose(): void {
    this.latest.clear();
    if (this.ringTimer) this.deps.timers.clear(this.ringTimer);
    if (this.answerTimer) this.deps.timers.clear(this.answerTimer);
    this.listeners.clear();
  }

  clearError(): void {
    this.set({ error: null });
  }

  // Events from the server

  onRing(call: CallInfo): void {
    this.latest.set(call.id, call);
    this.set({ history: upsert(this.state.history, call) });
    const me = this.deps.meId();
    if (call.ended_at || call.started_by === me || this.state.active?.call.id === call.id) return;
    const mine = call.participants.find((p) => p.user_id === me);
    if (mine && mine.state !== "ringing") return;
    this.set({ incoming: call });
    if (this.ringTimer) this.deps.timers.clear(this.ringTimer);
    // Ringing stops after 45 seconds even if the server's update is late.
    this.ringTimer = this.deps.timers.set(() => {
      if (this.state.incoming?.id === call.id) this.set({ incoming: null });
    }, RING_MS);
  }

  async onUpdated(call: CallInfo): Promise<void> {
    const me = this.deps.meId();
    this.latest.set(call.id, call);
    if (call.huddle) this.putHuddle(call);
    this.set({ history: upsert(this.state.history, call) });
    const incoming = this.state.incoming;
    if (incoming?.id === call.id) {
      const mine = call.participants.find((p) => p.user_id === me);
      // Answered or declined on another device, or the ring is over.
      if (call.ended_at || (mine && mine.state !== "ringing")) this.set({ incoming: null });
      else this.set({ incoming: call });
    }
    const active = this.state.active;
    if (active?.call.id === call.id) {
      if (call.ended_at) this.set({ active: null });
      else this.set({ active: { ...active, call } });
      if (call.answered_at && this.answerTimer) {
        this.deps.timers.clear(this.answerTimer);
        this.answerTimer = null;
      }
    }
    await this.reportEnd(call);
  }

  // Actions

  async startCall(chatId: string, media: CallMedia): Promise<ActiveCall> {
    const current = this.state.active;
    if (current) {
      if (current.chatId === chatId) return current;
      throw this.fail(BUSY);
    }
    this.set({ joining: chatId, error: null });
    try {
      const join = await this.deps.api.startCall(chatId, media);
      const me = this.deps.meId();
      let key = findCallKey(this.deps.messages(chatId), join.call);
      const joined = join.call.participants.filter((p) => p.state === "joined");
      const fresh = join.call.started_by === me && joined.length <= 1;
      if (!key && fresh) {
        key = this.deps.newKey();
        this.startedHere.add(join.call.id);
        await this.deps.send(chatId, {
          type: "call",
          call_id: join.call.id,
          media: join.call.media,
          event: "started",
          key,
        });
        const sent = this.deps
          .messages(chatId)
          ?.find((m) => m.content?.type === "call" && m.content.call_id === join.call.id && m.content.key);
        if (sent?.status === "failed") {
          await this.deps.api.leaveCall(join.call.id).catch(() => undefined);
          throw this.fail(KEY_NOT_SENT);
        }
      }
      if (!key) key = await this.waitForKey(chatId, join.call);
      if (!key) {
        await this.deps.api.leaveCall(join.call.id).catch(() => undefined);
        throw this.fail(NO_KEY);
      }
      const active = await this.activate(join, key, join.call.started_by === me);
      if (join.call.huddle) this.putHuddle(active.call);
      // A huddle waits for people to drop in, so it never hangs up unanswered.
      if (this.startedHere.has(join.call.id) && !active.call.answered_at && !join.call.huddle) {
        // Nobody answered within the ring time, so hang up. The chat then shows a missed call.
        this.answerTimer = this.deps.timers.set(() => {
          if (this.state.active?.call.id === join.call.id && !this.state.active.call.answered_at)
            void this.leaveCall();
        }, RING_MS);
      }
      return active;
    } catch (err) {
      throw err instanceof CallError ? err : this.fail(messageOf(err));
    } finally {
      this.set({ joining: null });
    }
  }

  async joinCall(callId: string): Promise<ActiveCall> {
    const current = this.state.active;
    if (current) {
      if (current.call.id === callId) return current;
      throw this.fail(BUSY);
    }
    this.set({ joining: callId, error: null });
    if (this.state.incoming?.id === callId) this.set({ incoming: null });
    try {
      const join = await this.deps.api.joinCall(callId);
      const key =
        findCallKey(this.deps.messages(join.call.chat_id), join.call) ??
        (await this.waitForKey(join.call.chat_id, join.call));
      if (!key) {
        await this.deps.api.leaveCall(callId).catch(() => undefined);
        throw this.fail(NO_KEY);
      }
      return await this.activate(join, key, join.call.started_by === this.deps.meId());
    } catch (err) {
      throw err instanceof CallError ? err : this.fail(messageOf(err));
    } finally {
      this.set({ joining: null });
    }
  }

  async leaveCall(): Promise<void> {
    const active = this.state.active;
    if (!active) return;
    if (this.answerTimer) this.deps.timers.clear(this.answerTimer);
    this.answerTimer = null;
    this.set({ active: null });
    try {
      const out = await this.deps.api.leaveCall(active.call.id);
      this.set({ history: upsert(this.state.history, out) });
      await this.reportEnd(out);
    } catch {
      // The server ends calls nobody is in, so a failed leave cleans itself up.
    }
  }

  /**
   * The page is closing, so it cannot wait for an answer. Tell the server now, or the other
   * person stays in a call with nobody until the server notices the empty room.
   */
  leaveOnExit(): void {
    const active = this.state.active;
    if (!active) return;
    void this.deps.api.leaveCall(active.call.id, { keepalive: true }).catch(() => undefined);
  }

  async declineCall(callId: string): Promise<void> {
    if (this.state.incoming?.id === callId) this.set({ incoming: null });
    const out = await this.deps.api.declineCall(callId);
    this.set({ history: upsert(this.state.history, out) });
  }

  async callHistory(): Promise<CallInfo[]> {
    const list = await this.deps.api.callHistory();
    this.set({ history: list });
    return list;
  }

  /** Asks whether a channel has a huddle on, for a channel opened after it started. */
  async loadHuddle(chatId: string): Promise<CallInfo | null> {
    const call = await this.deps.api.activeCall(chatId);
    if (call?.huddle) this.putHuddle(call);
    else if (this.state.huddles[chatId]) {
      const { [chatId]: _gone, ...rest } = this.state.huddles;
      this.set({ huddles: rest });
    }
    return call?.huddle ? call : null;
  }

  // Helpers

  private putHuddle(call: CallInfo) {
    const { [call.chat_id]: _old, ...rest } = this.state.huddles;
    this.set({ huddles: call.ended_at ? rest : { ...rest, [call.chat_id]: call } });
  }

  private async activate(join: CallJoin, key: string, outgoing: boolean): Promise<ActiveCall> {
    // Sharing the key takes a moment, and the call can be answered, declined or ended meanwhile.
    const call = this.newest(join.call);
    if (call.ended_at) {
      this.set({ history: upsert(this.state.history, call) });
      await this.reportEnd(call);
      throw this.fail(ENDED);
    }
    const active: ActiveCall = {
      call,
      chatId: join.call.chat_id,
      livekitUrl: join.livekit_url,
      token: join.token,
      key,
      outgoing,
      joinedAt: this.deps.now(),
    };
    this.set({ active, history: upsert(this.state.history, call) });
    return active;
  }

  private newest(call: CallInfo): CallInfo {
    const pushed = this.latest.get(call.id);
    if (!pushed || call.ended_at) return call;
    if (pushed.ended_at || (pushed.answered_at && !call.answered_at)) return pushed;
    return call;
  }

  /** Polls the chat for up to 10 seconds, for a joiner whose key message has not arrived yet. */
  private async waitForKey(chatId: string, call: CallInfo): Promise<string | null> {
    const deadline = this.deps.now() + KEY_WAIT_MS;
    for (;;) {
      await this.deps.sync(chatId).catch(() => undefined);
      const key = findCallKey(this.deps.messages(chatId), call);
      if (key) return key;
      if (this.deps.now() >= deadline) return null;
      await this.deps.sleep(POLL_MS);
    }
  }

  /** The caller's device puts one line in the chat when its call ends. */
  private async reportEnd(call: CallInfo): Promise<void> {
    if (!call.ended_at || !this.startedHere.has(call.id) || this.reported.has(call.id)) return;
    this.reported.add(call.id);
    // A huddle is never missed. It lasted from its start to its end.
    const duration = call.huddle
      ? Math.max(0, Math.round((Date.parse(call.ended_at) - Date.parse(call.started_at)) / 1000))
      : callDuration(call);
    const content: Content =
      duration === null
        ? { type: "call", call_id: call.id, media: call.media, event: "missed" }
        : { type: "call", call_id: call.id, media: call.media, event: "ended", duration };
    await this.deps.send(call.chat_id, content).catch(() => undefined);
  }

  private fail(message: string): CallError {
    this.set({ error: message });
    return new CallError(message);
  }
}

function messageOf(err: unknown): string {
  const detail = (err as { detail?: unknown }).detail;
  if (typeof detail === "string" && detail) return detail;
  return "Rhema could not connect the call. Check your connection and try again.";
}
